import React, { useEffect, useState } from "react";
import { View, StyleSheet, Pressable, ActivityIndicator } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import DatePickerInput from "react-native-paper-dates/src/Date/DatePickerInput";
import { Sparkle } from "@phosphor-icons/react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, Controller, useForm } from "react-hook-form";
import {
  useGenerateDueDatesForActivitiesMutation,
  useGetCourseByIdQuery,
  useUpdateCourseDatesMutation,
} from "../../../graphql/generated/graphql";
import NewCustomAxioActionModal from "../../settings/UsersTabContainer/NewCustomAxioActionModal";
import { useAppConfig } from "../../../AppConfigProvider";

enum ModalState {
  CLOSED = "CLOSED",
  PROCESSING = "PROCESSING",
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
  GENERATION_ERROR = "GENERATION_ERROR",
}

const modalTexts = {
  [ModalState.PROCESSING]: {
    title: "Updating Course Dates",
    ariaLabel: "Updating Course Dates",
    warningText: "Please wait while we update the course dates",
    actionButtonText: "Close",
  },
  [ModalState.SUCCESS]: {
    title: "Course Dates Updated",
    ariaLabel: "Course Dates Updated",
    warningText: "Course dates have been updated",
    actionButtonText: "Close",
  },
  [ModalState.ERROR]: {
    title: "Error Updating Course Dates",
    ariaLabel: "Error Updating Course Dates",
    warningText: "An error occurred while updating the course dates",
    actionButtonText: "Close",
  },
  [ModalState.GENERATION_ERROR]: {
    title: "Error Generating Due Dates",
    ariaLabel: "Error Generating Due Dates",
    warningText: "An error occurred while generating the due dates",
    actionButtonText: "Close",
  },
};

const schema = z
  .object({
    startDate: z.date().nullable(),
    endDate: z.date().nullable(),
    dueDateStartDate: z.date().nullable(),
    dueDateEndDate: z.date().nullable(),
  })
  .refine(
    (data) => {
      // Only validate if both dates exist
      if (!data.startDate || !data.endDate) return true;
      return new Date(data.startDate) <= new Date(data.endDate);
    },
    {
      message: "Start date must be before or equal to end date",
      path: ["startDate", "endDate"],
    }
  )
  .refine(
    (data) => {
      // Only validate if both dates exist
      if (!data.dueDateStartDate || !data.dueDateEndDate) return true;
      return new Date(data.dueDateStartDate) <= new Date(data.dueDateEndDate);
    },
    {
      message: "Due Date Start must be before or equal to Due Date End",
      path: ["dueDateStartDate", "dueDateEndDate"],
    }
  );

const DueDateControlsContainer = ({ courseId }: { courseId: string }) => {
  const { primaryColor } = useAppConfig();

  const { data: courseData, refetch: refetchCourseData } = useGetCourseByIdQuery({
    variables: {
      id: courseId,
    },
  });

  const [updateCourseDates] = useUpdateCourseDatesMutation();
  const [generateDueDatesForActivities] = useGenerateDueDatesForActivitiesMutation();

  const [isGeneratingDueDates, setIsGeneratingDueDates] = useState(false);
  const [canGenerateDueDates, setCanGenerateDueDates] = useState(false);

  const [modalState, setModalState] = useState<ModalState>(ModalState.CLOSED);

  const { control, handleSubmit, setValue, formState, reset } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      startDate: null,
      endDate: null,
      dueDateStartDate: null,
      dueDateEndDate: null,
    },
  });

  useEffect(() => {
    if (courseData?.getCourseById?.data?.[0]) {
      const course = courseData.getCourseById.data[0];

      if (course.startDate) setValue("startDate", new Date(courseData.getCourseById.data[0].startDate));

      if (course.endDate) setValue("endDate", new Date(courseData.getCourseById.data[0].endDate));

      if (course.dueDateStartDate)
        setValue("dueDateStartDate", new Date(courseData.getCourseById.data[0].dueDateStartDate));

      if (course.dueDateEndDate) setValue("dueDateEndDate", new Date(courseData.getCourseById.data[0].dueDateEndDate));
    }
  }, [courseData]);

  useEffect(() => {
    if (courseData?.getCourseById?.data?.[0]) {
      const course = courseData.getCourseById.data[0];
      setCanGenerateDueDates(course.dueDateStartDate && course.dueDateEndDate);
    }
  }, [courseData]);

  const handleUpdateCourseDates = async () => {
    if (formState.isValid) {
      setModalState(ModalState.PROCESSING);
    }

    handleSubmit(async (data) => {
      const result = await updateCourseDates({
        variables: {
          id: courseId,
          input: {
            startDate: data.startDate ? new Date(data.startDate).toISOString() : null,
            endDate: data.endDate ? new Date(data.endDate).toISOString() : null,
            dueDateStartDate: data.dueDateStartDate ? new Date(data.dueDateStartDate).toISOString() : null,
            dueDateEndDate: data.dueDateEndDate ? new Date(data.dueDateEndDate).toISOString() : null,
          },
        },
      });

      if (result.data?.updateCourseDates.success) {
        setModalState(ModalState.SUCCESS);
      } else {
        setModalState(ModalState.ERROR);
      }
      reset();
      await refetchCourseData();
    })();
  };

  const handleGenerateDueDates = async () => {
    setIsGeneratingDueDates(true);

    const result = await generateDueDatesForActivities({
      variables: {
        courseId,
      },
    });

    if (result.data?.generateDueDatesForActivities.success) {
      setIsGeneratingDueDates(false);
    } else {
      setIsGeneratingDueDates(false);
      setModalState(ModalState.GENERATION_ERROR);
    }

    await refetchCourseData();
  };

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        <CustomText text="Course Start and End Dates" size="m" weight="ultraBold" style={styles.sectionTitle} />
        <CustomText
          text="These dates determine when your course becomes available to students and when it will close."
          size="xs"
          style={styles.sectionDescription}
        />
        <CustomSpacing type="vertical" size="m" />
        <View style={styles.row}>
          <View style={styles.dateFieldContainer}>
            <CustomText text="Start Date" size="xs" weight="bold" />
            <Controller
              control={control}
              name="startDate"
              render={({ field: { onChange, onBlur, value } }) => (
                <DatePickerInput
                  locale="en"
                  label={undefined}
                  value={value}
                  onChange={(date) => {
                    onChange(date);
                    onBlur();
                  }}
                  inputMode="start"
                  mode="outlined"
                  withDateFormatInLabel={false}
                  outlineColor={"#D1D5DB"}
                  activeOutlineColor={"blue"}
                  inputProps={{
                    color: "#DEDEDE",
                    mode: "outline",
                  }}
                  theme={{
                    colors: "#000000",
                    roundness: 8,
                    opacity: 1,
                  }}
                  aria-label="Birthday date picker"
                />
              )}
            />
          </View>
          <View style={styles.dateFieldContainer}>
            <CustomText text="End Date" size="xs" weight="bold" />
            <Controller
              control={control}
              name="endDate"
              render={({ field: { onChange, onBlur, value } }) => (
                <DatePickerInput
                  locale="en"
                  label={undefined}
                  value={value}
                  onChange={(date) => {
                    onChange(date);
                    onBlur();
                  }}
                  inputMode="end"
                  mode="outlined"
                  withDateFormatInLabel={false}
                  outlineColor={"#D1D5DB"}
                  activeOutlineColor={"blue"}
                  inputProps={{
                    color: "#DEDEDE",
                    mode: "outline",
                  }}
                  theme={{
                    colors: "#000000",
                    roundness: 8,
                    opacity: 1,
                  }}
                  aria-label="End date picker"
                />
              )}
            />
          </View>
          <View style={{ paddingTop: 20 }}>
            <CustomButton
              text="Set"
              styleType="primary"
              size="m"
              onPress={handleUpdateCourseDates}
              style={{
                backgroundColor: !formState.isValid || !formState.isDirty ? "#D1D5DB" : primaryColor,
                alignSelf: "flex-end",
              }}
              disabled={!formState.isValid || !formState.isDirty}
            />
          </View>
        </View>
        <View style={styles.divider} />
        <CustomText
          text="Update Due Dates for Course Activities"
          size="m"
          weight="ultraBold"
          style={styles.sectionTitle}
        />
        <CustomText
          text="Based on your selected start and end dates, Axio will automatically generate evenly spaced due dates across all modules and activities. You can adjust any due date manually afterward."
          size="xs"
          style={styles.sectionDescription}
        />
        <CustomSpacing type="vertical" size="m" />
        <View style={styles.row}>
          <View style={styles.dateFieldContainer}>
            <CustomText text="Start Due Date" size="xs" weight="bold" />
            <Controller
              control={control}
              name="dueDateStartDate"
              render={({ field: { onChange, onBlur, value } }) => (
                <DatePickerInput
                  locale="en"
                  label={undefined}
                  value={value}
                  onChange={(date) => {
                    onChange(date);
                    onBlur();
                  }}
                  inputMode="start"
                  mode="outlined"
                  withDateFormatInLabel={false}
                  outlineColor={"#D1D5DB"}
                  activeOutlineColor={"blue"}
                  inputProps={{
                    color: "#DEDEDE",
                    mode: "outline",
                  }}
                  theme={{
                    colors: "#000000",
                    roundness: 8,
                    opacity: 1,
                  }}
                  aria-label="Start due date picker"
                />
              )}
            />
          </View>
          <View style={styles.dateFieldContainer}>
            <CustomText text="End Due Date" size="xs" weight="bold" />
            <Controller
              control={control}
              name="dueDateEndDate"
              render={({ field: { onChange, onBlur, value } }) => (
                <DatePickerInput
                  locale="en"
                  label={undefined}
                  value={value}
                  onChange={(date) => {
                    onChange(date);
                    onBlur();
                  }}
                  inputMode="end"
                  mode="outlined"
                  withDateFormatInLabel={false}
                  outlineColor={"#D1D5DB"}
                  activeOutlineColor={"blue"}
                  inputProps={{
                    color: "#DEDEDE",
                    mode: "outline",
                  }}
                  theme={{
                    colors: "#000000",
                    roundness: 8,
                    opacity: 1,
                  }}
                  aria-label="End due date picker"
                />
              )}
            />
          </View>
          <View style={{ paddingTop: 20 }}>
            <CustomButton
              text="Set"
              styleType="primary"
              size="m"
              onPress={handleUpdateCourseDates}
              style={{
                backgroundColor: !formState.isValid || !formState.isDirty ? "#D1D5DB" : primaryColor,
                alignSelf: "flex-end",
              }}
              disabled={!formState.isValid || !formState.isDirty}
            />
          </View>
        </View>
        <CustomSpacing type="vertical" size="m" />
        <CustomText
          text="This action regenerates due dates for all activities and overwrites all existing due dates"
          size="xs"
          style={styles.sectionDescription}
        />
        {isGeneratingDueDates && (
          <CustomButton
            leftIcon={<ActivityIndicator size="small" color={primaryColor} />}
            text="Generating..."
            size="m"
            styleType="primaryLight"
            style={{ width: "fit-content" }}
            disabled={true}
            onPress={() => {}}
          />
        )}
        {!isGeneratingDueDates && (
          <CustomButton
            text="Generate Due Dates"
            styleType="primaryLight"
            size="m"
            onPress={handleGenerateDueDates}
            textStyle={{
              color: !(!canGenerateDueDates || formState.isDirty) ? primaryColor : "#D1D5DB",
            }}
            style={{ width: "fit-content", gap: 8 }}
            leftIcon={
              <Sparkle
                size={16}
                weight="bold"
                color={!(!canGenerateDueDates || formState.isDirty) ? primaryColor : "#D1D5DB"}
              />
            }
            rightIcon={null}
            textProps={{}}
            useTranslationText={true}
            disabled={!canGenerateDueDates || formState.isDirty}
          />
        )}

        <CustomSpacing type="vertical" size="l" />
        {!formState.isValid && formState.isDirty && (
          <CustomText
            text={
              "Please check your dates and try again. Start Date should be before End Date, and Start Due Date should be before End Due Date."
            }
            size="xs"
            style={styles.warningText}
          />
        )}
      </View>

      <NewCustomAxioActionModal
        visible={modalState === ModalState.PROCESSING}
        onClose={() => setModalState(ModalState.CLOSED)}
        modalTexts={modalTexts.PROCESSING}
      />
      <NewCustomAxioActionModal
        visible={modalState === ModalState.SUCCESS}
        onClose={() => setModalState(ModalState.CLOSED)}
        modalTexts={modalTexts.SUCCESS}
      />
      <NewCustomAxioActionModal
        visible={modalState === ModalState.ERROR}
        onClose={() => setModalState(ModalState.CLOSED)}
        modalTexts={modalTexts.ERROR}
      />
      <NewCustomAxioActionModal
        visible={modalState === ModalState.GENERATION_ERROR}
        onClose={() => setModalState(ModalState.CLOSED)}
        modalTexts={modalTexts.GENERATION_ERROR}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    backgroundColor: "#fff",
    width: 600,
    marginHorizontal: "auto",
    flexDirection: "column",
  },
  card: {
    flexDirection: "column",
    justifyContent: "flex-start",
    maxWidth: 600,
    padding: 24,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 12,
    backgroundColor: "#fff",
  },
  sectionTitle: {
    marginBottom: 4,
  },
  sectionDescription: {
    color: "#6B7280",
    marginBottom: 8,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 32,
    marginBottom: 16,
  },
  dateFieldContainer: {
    flex: 1,
    minWidth: 180,
    maxWidth: 220,
    gap: 4,
  },

  divider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginVertical: 24,
    width: "100%",
  },
  warningText: {
    color: "#EF4444",
    marginBottom: 4,
  },
});

export default DueDateControlsContainer;
